import { type ReactElement, useEffect, useState } from 'react';
import type { OpsRideThread } from '@vello/api-client';
import { Badge, Button, Panel, Text, Stack, Ink } from '@vello/web-theme';
import { formatMarketInstant, isReplyToCentral, marketTimeZone, rideChatAuthority } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { PersonName } from './person-name';

export function RideChatHistoryPanel({ rideId, marketId }: { readonly rideId: string; readonly marketId: string }): ReactElement | null {
  const { runtime, grants, markets } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v } = useVocab();
  const authority = rideChatAuthority(grants);
  const [thread, setThread] = useState<OpsRideThread | undefined>(undefined);
  const timeZone = marketTimeZone(marketId, markets);

  useEffect(() => {
    if (!authority.mayView) return undefined;
    let live = true;
    void runtime.centralDesk.rideThread(rideId).then((r) => {
      if (live) setThread(r.ok ? r.value : undefined);
    });
    return () => {
      live = false;
    };
  }, [runtime, rideId, authority.mayView]);

  if (!authority.mayView || thread === undefined || thread.messages.length === 0) return null;

  return (
    <Stack as="section" gap={3} aria-labelledby="ride-chat-history-title">
      <Text as="h2" role="title" id="ride-chat-history-title">{t('cdesk.history.title')}</Text>
      <Panel><Stack gap={4}>
        <Stack as="ol" gap={3}>
          {thread.messages.map((m) => (
            <li key={m.id} data-testid={`ride-chat-${m.senderRole}`}>
              <Text as="div" role="caption" tone="secondary">
                <strong>{m.senderRole === 'operator' ? <>{v('central_desk.chat_sender_role', 'operator')} · <PersonName id={m.operatorId} /></> : v('central_desk.chat_sender_role', m.senderRole)}</strong>
                {m.recipient !== undefined && <> → {v('central_desk.chat_recipient', m.recipient)}</>}
                {isReplyToCentral(m) && <> <Badge tone="warning">{t('cdesk.thread.replyToCentral')}</Badge></>}
                <span className="tnum"> · {formatMarketInstant(m.createdAt, timeZone, lang, 'datetime-seconds')}</span>
              </Text>
              <Ink as="div" preLine breakAnywhere>{m.body}</Ink>
            </li>
          ))}
        </Stack>
        <div>
          <Button variant="secondary" onClick={() => navigate('ride_chats', { ride: rideId })}>{t(thread.chat.open ? 'cdesk.history.answer' : 'cdesk.history.open')}</Button>
        </div>
      </Stack></Panel>
    </Stack>
  );
}
